import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/* ══════════════════════════════════════════════════════
   USER STORE — Navigation, profiles, preferences
   ══════════════════════════════════════════════════════ */

export const useUserStore = create(
  persist(
    (set) => ({
      // ── Navigation ──
      activeTab: 'home',
      setActiveTab: (activeTab) => set({ activeTab }),

      // ── Transient cross-tab focus intent ──
      // Set by a navigation source ("take me to wealth tracker AND scroll to
      // the mortgage form"). Consumed and cleared by the destination view.
      // NOT persisted (intentionally absent from partialize).
      pendingFocus: null,
      setPendingFocus: (pendingFocus) => set({ pendingFocus }),
      clearPendingFocus: () => set({ pendingFocus: null }),

      // ── View State ──
      month: new Date().getMonth(),
      year: new Date().getFullYear(),
      setMonth: (month) => set({ month }),
      setYear: (year) => set({ year }),
      prevMonth: () => set(state => {
        if (state.month === 0) return { month: 11, year: state.year - 1 };
        return { month: state.month - 1 };
      }),
      nextMonth: () => set(state => {
        if (state.month === 11) return { month: 0, year: state.year + 1 };
        return { month: state.month + 1 };
      }),

      // ── Plan Type ──
      // DEPRECATED — see useEntitlement(). Kept transitionally for legacy reads.
      planType: 'personal',
      setPlanType: (planType) => set({ planType }),
      isBusiness: false,
      setIsBusiness: (isBusiness) => set(state => {
        const nextIsBusiness = isBusiness === true;
        const nextActiveMode = (
          !nextIsBusiness && state.activeMode === 'business'
        ) ? 'personal' : state.activeMode;
        return {
          isBusiness: nextIsBusiness,
          // WHY: disabling the only Business entitlement while its ledger is
          // active otherwise strands every primary nav button on hidden Business
          // routes with no mode switch left to escape. Keep the data, but return
          // the visible surface to Personal.
          ...(nextActiveMode !== state.activeMode
            ? { activeMode: nextActiveMode }
            : {}),
        };
      }),
      isFamily: false,
      setIsFamily: (isFamily) => set({ isFamily }),

      // ── First setup lane ──
      // null until a brand-new user explicitly chooses Personal or Business.
      // This is intentionally separate from activeMode: setupMode records the
      // onboarding promise, while activeMode can change later in the app.
      setupMode: null,
      setSetupMode: (setupMode) => {
        if (setupMode !== 'personal' && setupMode !== 'business' && setupMode !== null) return;
        set(state => ({
          setupMode,
          ...(setupMode === 'business' ? {
            planType: 'business',
            isBusiness: true,
            activeMode: 'business',
            unlockedFeatures: {
              ...state.unlockedFeatures,
              business: true,
            },
          } : setupMode === 'personal' ? {
            planType: 'personal',
            isBusiness: false,
            activeMode: 'personal',
          } : {}),
        }));
      },

      // ── Active Surface Mode (Phase 4 — mode-aware Today + witnesses) ──
      // 'personal' | 'business' | 'household'. The mode pill on Today
      // toggles this. Witness rules read it from useWitnessContext to
      // filter which observations fire (money rules are personal-only).
      // Default 'personal' for every user, including legacy.
      activeMode: 'personal',
      setActiveMode: (mode) => {
        if (mode !== 'personal' && mode !== 'business' && mode !== 'household') return;
        set(state => {
          if (mode === 'business' && !state.isBusiness) return state;
          if (mode === 'household' && !state.isFamily) return state;
          return { activeMode: mode };
        });
      },

      // ── New Today screen (Phase 5 preview flag) ──
      // When true, the home tab renders TodayHome (witness hero pinned
      // at top + mode switcher + customizable Dashboard wrapped below).
      // When false, the home tab renders the existing Dashboard
      // unchanged. Defaults to false so existing users don't get an
      // unexpected layout shift on next open. Users opt in via
      // Settings → "Try the new Today screen".
      useNewHomeScreen: true,
      setUseNewHomeScreen: (v) => set({ useNewHomeScreen: !!v }),
      // Business → Personal bridge config (Phase 0.5).
      // Graduated from a simple boolean to a config object so the allocation
      // engine in businessStore.logBizIncome knows where to route each slice
      // of a payment (tax → taxReserve, profit → a bucket, etc.).
      bizBridge: {
        enabled: false,
        ownerPayToPersonalIncome: true,
        profitBucketId: null,
        opexBucketId: null,
        autoLogTaxPayment: true,
      },
      setBizBridge: (updates) => set(state => ({
        bizBridge: typeof updates === 'object' && updates !== null
          ? { ...state.bizBridge, ...updates }
          // Legacy-style boolean calls still work: setBizBridge(true/false)
          // updates only the `enabled` flag, preserving other config.
          : { ...state.bizBridge, enabled: !!updates },
      })),

      // ── Onboarding ──
      onboarded: false,
      setOnboarded: (onboarded) => set({ onboarded }),
      onboardingStep: 0,
      setOnboardingStep: (onboardingStep) => set({ onboardingStep }),

      // ── Origin (captured once at signup, immutable) ──
      // The pre-step that runs before Step 1 in OnboardingFlow. Mirrors
      // the user_origins row in Supabase (server is source of truth; this
      // boolean lets the client know whether to render Step0Origin again).
      originSubmitted: false,
      setOriginSubmitted: (originSubmitted) => set({ originSubmitted }),

      // ── Identity (onboarding Q1) ──
      // 'self_employed' | 'salaried' | 'both' | null. Drives whether the
      // profession step renders during onboarding and whether the reveal
      // screen shows the tax-adjusted number for Business+ tier users.
      identity: null,
      setIdentity: (identity) => set({ identity }),

      // ── Profession Type ──
      professionType: null,
      setProfessionType: (professionType) => set({ professionType }),

      // ── Progressive Feature Unlocking ──
      unlockedFeatures: {
        budgets: false,
        goals: false,
        journey: true,
        reports: false,
        business: false,
      },
      unlockFeature: (feature) => set(state => ({
        unlockedFeatures: { ...state.unlockedFeatures, [feature]: true },
      })),

      totalTxnCount: 0,
      incrementTxnCount: () => set(state => ({
        totalTxnCount: state.totalTxnCount + 1,
      })),

      // ── Witness one-shot flags ──
      // firstTxnWitnessShown: flipped by WitnessSlot the first time the
      // first.transaction archetype renders. The rule then goes silent
      // forever for that user. Initial false; never reset.
      firstTxnWitnessShown: false,
      markFirstTxnWitnessShown: () => set({ firstTxnWitnessShown: true }),

      // lastSeenLetterUnlockId: id of the most recent letter whose unlock
      // witness was shown to the user. The letterUnlock rule stays silent
      // when this matches the candidate letter, even within the freshness
      // window. Survives the LetterUnlockModal acknowledge action because
      // the modal is the primary surface — the dashboard line is the
      // backup if they dismissed/missed the modal.
      lastSeenLetterUnlockId: null,
      setLastSeenLetterUnlockId: (id) => set({ lastSeenLetterUnlockId: id }),

      // ── Profiles (Family Plan) ──
      profiles: [],
      activeProfileId: null,
      setProfiles: (profiles) => set({ profiles }),
      setActiveProfileId: (activeProfileId) => set({ activeProfileId }),
      addProfile: (profile) => set(state => ({
        profiles: [...state.profiles, profile],
      })),

      // ── Settings ──
      displayName: '',
      setDisplayName: (displayName) => set({ displayName }),
      avatarUrl: '',
      setAvatarUrl: (avatarUrl) => set({ avatarUrl }),
      bio: '',
      setBio: (bio) => set({ bio: bio.slice(0, 160) }),
      pinEnabled: false,
      setPinEnabled: (pinEnabled) => set({ pinEnabled }),
      pin: null,
      setPin: (pin) => set({ pin }),

      // ── 1099 tax set-aside (personal tier) ──
      incomeTypeBackfilled: false,
      setIncomeTypeBackfilled: (v) => set({ incomeTypeBackfilled: !!v }),
      incomeTypeBackfillBannerDismissed: false,
      setIncomeTypeBackfillBannerDismissed: (v) => set({ incomeTypeBackfillBannerDismissed: !!v }),

      // ── Onboarding Hints ──
      dismissedHints: {},
      dismissHint: (id) => set(state => ({
        dismissedHints: { ...state.dismissedHints, [id]: true },
      })),

      // ── Day-1 Dashboard hints ──
      // Set to true after the user dismisses the drag/resize/customize hint
      // banner that appears on Dashboard for new users. Persists across reloads.
      dashboardHintDismissed: false,
      setDashboardHintDismissed: (v) => set({ dashboardHintDismissed: !!v }),

      // ── Contextual "Talk to Morgan" prompts ──
      // Per-trigger dismissal state. Keys: "debt-plan-created",
      // "big-goal-created", "business-mode-on", "streak-30".
      // Values: true = user has seen and dismissed (or clicked through).
      dismissedMorganPrompts: {},
      dismissMorganPrompt: (id) => set(state => ({
        dismissedMorganPrompts: { ...state.dismissedMorganPrompts, [id]: true },
      })),

      // ── Subscription Tier ──
      // DEPRECATED — mirrored from useEntitlement() (src/hooks/useEntitlement.js).
      // Do not write directly; do not read for new code (use useEntitlement instead).
      // Slated for removal in a follow-up cleanup pass once all call sites migrate.
      tier: 'free',
      setTier: (tier) => set({ tier }),

      // ── Coach Preferences ──
      coachJournalAccess: false,
      setCoachJournalAccess: (coachJournalAccess) => set({ coachJournalAccess }),

      // ── Modals ──
      activeModal: null,
      setActiveModal: (activeModal) => set({ activeModal }),
      closeModal: () => set({ activeModal: null }),

      // ── Rewind card preferences ──
      rewindCardView: 'week',                                    // 'week' | 'month'
      setRewindCardView: (rewindCardView) => set({ rewindCardView }),
      rewindCardHidden: false,
      setRewindCardHidden: (rewindCardHidden) => set({ rewindCardHidden }),
      lastRewindShown: { week: null, month: null },
      setLastRewindShown: (period, key) => set(state => ({
        lastRewindShown: { ...state.lastRewindShown, [period]: key }
      })),
    }),
    {
      name: 'karat-user',
      version: 9,
      migrate: (persisted, fromVersion) => {
        if (fromVersion < 2) {
          persisted.avatarUrl = persisted.avatarUrl || '';
          persisted.bio = persisted.bio || '';
        }
        if (fromVersion < 3) {
          // v2 → v3: bizBridge graduates from boolean to config object.
          // Preserve the user's existing on/off state in .enabled so any
          // current bridge-on users don't lose their setting.
          const legacy = persisted.bizBridge;
          persisted.bizBridge = {
            enabled: typeof legacy === 'boolean' ? legacy : !!legacy?.enabled,
            ownerPayToPersonalIncome: legacy?.ownerPayToPersonalIncome ?? true,
            profitBucketId: legacy?.profitBucketId ?? null,
            opexBucketId: legacy?.opexBucketId ?? null,
            autoLogTaxPayment: legacy?.autoLogTaxPayment ?? true,
          };
        }
        if (fromVersion < 4) {
          // v3 → v4: existing users with totalTxnCount ≥ 1 already passed
          // the first-transaction moment; treat the witness as already shown
          // so they aren't surprised by a stale "Day one" line months later.
          persisted.firstTxnWitnessShown =
            (persisted.totalTxnCount || 0) >= 1;
        }
        if (fromVersion < 5) {
          // v4 → v5: introduce letter.unlock witness one-shot.
          // Default null = "no unlock yet seen". The freshness window
          // (7 days post-unlock) bounds backfill risk for any users
          // who already acknowledged a letter modal pre-witness-rule.
          persisted.lastSeenLetterUnlockId = persisted.lastSeenLetterUnlockId ?? null;
        }
        if (fromVersion < 6) {
          // v5 → v6: introduce activeMode for mode-aware Today + witnesses.
          // Default everyone to 'personal' — money rules behave exactly
          // as they always have. Business owners + family users opt
          // into 'business'/'household' explicitly via the mode pill.
          persisted.activeMode = persisted.activeMode || 'personal';
        }
        if (fromVersion < 7) {
          // v6 → v7: introduce useNewHomeScreen preview flag. Default
          // false so existing users see the same Dashboard they always
          // have. Opt-in via Settings.
          persisted.useNewHomeScreen = persisted.useNewHomeScreen ?? false;
        }
        if (fromVersion < 8) {
          // v7 → v8: flip useNewHomeScreen default from false to true
          // (the new IA is now the canonical home). Respect any user who
          // explicitly toggled it off — only flip when the field is
          // genuinely unset, not when it's an explicit `false`.
          if (persisted.useNewHomeScreen === undefined || persisted.useNewHomeScreen === null) {
            persisted.useNewHomeScreen = true;
          }
        }
        if (fromVersion < 9) {
          // Existing accounts already passed setup. Derive their lane without
          // forcing onboarding back open; only a truly new store begins null.
          persisted.setupMode = persisted.setupMode || (
            persisted.isBusiness || persisted.activeMode === 'business'
              ? 'business'
              : 'personal'
          );
        }
        return persisted;
      },
      partialize: (state) => ({
        planType: state.planType,
        isBusiness: state.isBusiness,
        isFamily: state.isFamily,
        setupMode: state.setupMode,
        bizBridge: state.bizBridge,
        activeMode: state.activeMode,
        useNewHomeScreen: state.useNewHomeScreen,
        onboarded: state.onboarded,
        onboardingStep: state.onboardingStep,
        originSubmitted: state.originSubmitted,
        identity: state.identity,
        unlockedFeatures: state.unlockedFeatures,
        totalTxnCount: state.totalTxnCount,
        firstTxnWitnessShown: state.firstTxnWitnessShown,
        lastSeenLetterUnlockId: state.lastSeenLetterUnlockId,
        profiles: state.profiles,
        activeProfileId: state.activeProfileId,
        displayName: state.displayName,
        avatarUrl: state.avatarUrl,
        bio: state.bio,
        pinEnabled: state.pinEnabled,
        pin: state.pin,
        dismissedHints: state.dismissedHints,
        dashboardHintDismissed: state.dashboardHintDismissed,
        professionType: state.professionType,
        tier: state.tier,
        coachJournalAccess: state.coachJournalAccess,
        dismissedMorganPrompts: state.dismissedMorganPrompts,
        incomeTypeBackfilled: state.incomeTypeBackfilled,
        incomeTypeBackfillBannerDismissed: state.incomeTypeBackfillBannerDismissed,
        rewindCardView: state.rewindCardView,
        rewindCardHidden: state.rewindCardHidden,
        lastRewindShown: state.lastRewindShown,
      }),
    }
  )
);
